import React from "react";
import ObjectUtils, { coalesce } from "../../../../utils/ObjectUtils";
import { findAnswer, isAnswered, makeMatrixAnswer } from "../../../../utils/QuestionUtils";

import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import Col from "react-bootstrap/Col";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from "react-bootstrap/Row";
import BootstrapTable from "react-bootstrap/Table";
import Tooltip from "react-bootstrap/Tooltip";
import { injectIntl } from "react-intl";
import Checkbox from "../../../common/Checkbox";
import Radio from "../../../common/Radio";
import QuestionHeader from "./QuestionHeader";

const { opt, getDeepProperty } = ObjectUtils;

const InfoTip = (text) =>
  opt(text).length > 0 && (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="tooltip" className="survey-tooltip">
          {text}
        </Tooltip>
      }
    >
      <FontAwesomeIcon icon={faInfoCircle} />
    </OverlayTrigger>
  );

const MatrixColumn = ({ choice }) => (
  <th>
    {`${choice.text} `}
    {InfoTip(choice.infoText)}
  </th>
);

const MatrixCell = (props) => {
  const {
    row,
    choice,
    questionAnswer,
    disabled,
    question,
    removeQuestionAnswer,
    addQuestionAnswer,
    setQuestionAnswer
  } = props;

  const answer = findAnswer(questionAnswer.answers, {
    row,
    value: choice.value
  });

  const attrs = answer ? { checked: true } : {};

  return (
    <td className="matrix-checkbox">
      {question.type === "matrixSingleChoice" ? (
        <Radio
          disabled={disabled}
          checked={Boolean(answer)}
          onClick={() => {
            if (attrs.checked) {
              removeQuestionAnswer(question, makeMatrixAnswer(choice.value, row, choice));
            } else {
              setQuestionAnswer(question, makeMatrixAnswer(choice.value, row, choice));
            }
          }}
        />
      ) : (
        <Checkbox
          disabled={disabled}
          onChange={() => {
            if (answer) {
              removeQuestionAnswer(question, makeMatrixAnswer(choice.value, row, choice));
            } else {
              addQuestionAnswer(question, makeMatrixAnswer(choice.value, row, choice));
            }
          }}
          {...attrs}
        />
      )}
    </td>
  );
};

const MatrixRow = (props) => {
  const { skipFirstRow, row, question } = props;

  return (
    <tr>
      {!skipFirstRow && (
        <td>
          {row.text}
          {InfoTip(row.infoText)}
        </td>
      )}
      {coalesce(question.choices, []).map((c, i) => (
        <MatrixCell key={i} row={row} choice={c} {...props} />
      ))}
    </tr>
  );
};

/**
 * Matrix type question component for survey-view (matrix single choice and matrix multi-choice).
 */
const SingleMatrixQuestion = (props) => {
  const { questionAnswer, question } = props;

  const skipFirstRow = !coalesce(getDeepProperty(question, "rows"), []).some(
    (row) => row.text !== null && row.text !== undefined
  );

  return (
    <Row>
      <Col lg={12} md={12} sm={12}>
        <QuestionHeader question={question} answered={isAnswered(question, questionAnswer)}>
          <BootstrapTable responsive>
            <thead>
              <tr>
                {!skipFirstRow && <th></th>}
                {question.choices.map((c, i) => (
                  <MatrixColumn key={i} choice={c} {...props} />
                ))}
              </tr>
            </thead>
            <tbody>
              {question.rows.map((r, i) => (
                <MatrixRow key={i} skipFirstRow={skipFirstRow} row={r} {...props} />
              ))}
            </tbody>
          </BootstrapTable>
        </QuestionHeader>
      </Col>
    </Row>
  );
};

SingleMatrixQuestion.propTypes = {
  disabled: PropTypes.bool,
  question: PropTypes.object,
  questionAnswer: PropTypes.object,
  removeQuestionAnswer: PropTypes.func,
  addQuestionAnswer: PropTypes.func,
  setQuestionAnswer: PropTypes.func
};

export default injectIntl(SingleMatrixQuestion);
